
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import './../slider/slider.scss'; // Default slider styles. Override these per component basis.

/**
 * Product slider
 * @class
 *
 * @param {DOMElement} element - Product slider element .product-slider
 * @param {Object} options - Options
 *
 * Example with nav outside swiper element:
 *
 * <section class="slider-container">
 *  <div class="product-slider-nav slider-container__nav" data-slider="1">
 *    <h2 class="product-slider-nav-title" data-title="Swipe to see more products %number% results"></h2>
 *  </div>
 * <div class="slider-container__wrapper">
 *    <div class="product-slider slider-container__group" data-slider-count="1" data-prev-button-content="Previous" data-next-button-content="Next">
 *     <div class="products-list">
 *       <div class="product-card">...</div>
 *       <div class="product-card">...</div>
 *      </div>
 *   </div>
 * </div>
 * </section>
 */

export class ProductSlider {
  #element;
  #sliderIndex;
  swiper;

  constructor(element) {
    if (element) {
      // Initialize only if more than 1 slide
      const slides = element.querySelectorAll('.products-list > *') as HTMLElement[];
      if (slides.length < 2) {
        return;
      }

      this.#element = element;
      if (this.#element.dataset.sliderCount) {
        this.#sliderIndex = this.#element.dataset.sliderCount;
      }

      // Add relevant classes
      element.classList.add('swiper');
      element.parentNode.classList.add('swiper-wrapper-initialized');
      const wrapper = element.querySelector('.products-list');
      wrapper.classList.add('swiper-wrapper');

      Array.from(slides).forEach(slide => {
        const slideWrapper = document.createElement('div');
        slideWrapper.classList.add('swiper-slide');
        slide.parentNode?.insertBefore(slideWrapper, slide);
        slideWrapper.appendChild(slide);
      });

      this.swiper = new Swiper(element, {
        navigation: {
          nextEl: '.slider-container-next',
          prevEl: '.slider-container-prev',
        },
        modules: [Navigation],
        slidesPerView: 1,
        spaceBetween: 15,
        speed: 400,
        centeredSlides: false,
        breakpoints: {
          720: {
            slidesPerView: 2,
            slidesPerGroup: 2,
            spaceBetween: 20,
          },
          992: {
            slidesPerView: 3,
            slidesPerGroup: 3,
            spaceBetween: 30,
          },
          1500: {
            slidesPerView: 4,
            slidesPerGroup: 4,
            spaceBetween: 64,
          },
        },
        on: {
          beforeInit: () => {
            this.createNav();
          },
        },

      });

      Array.from(slides).forEach((slide, slideIndex) => {
        slide.addEventListener('focusin', () => {
          const slideX = (slide.parentNode as HTMLElement)?.offsetLeft;
          const sliderOffsetX = Math.abs(this.swiper.getTranslate());
          const visibleWidth = this.swiper.width;

          if (slideX > sliderOffsetX + visibleWidth || slideX < sliderOffsetX) {
            this.swiper.slideTo(slideIndex)
          }
        })
      });
    }
  }

  /**
   * Creates swiper next/prev navigation and title dynamically
   */
  createNav() {
    const slides = this.#element.querySelectorAll('.swiper-slide');

    const prevButton = document.createElement('button');
    ['slider-container-prev', 'button', 'button--ghost', 'button--icon'].forEach(className => prevButton.classList.add(className));
    prevButton.innerHTML = this.#element.dataset.prevButtonContent;

    const nextButton = document.createElement('button');
    ['slider-container-next', 'button', 'button--ghost', 'button--icon'].forEach(className => nextButton.classList.add(className));
    nextButton.innerHTML = this.#element.dataset.nextButtonContent;

    // Nav can be inside our outside swiper-element
    // If outside, nav with class .product-slider-nav needs [data-slider] to match swiper-element's (.product-slider) [data-slider-count]
    const navEl = this.#element.querySelector('.product-slider-nav') as HTMLElement || Array.from(document.querySelectorAll('.product-slider-nav')).find(el => (el as HTMLElement).dataset.slider === this.#sliderIndex) as HTMLElement;
    const title = navEl.querySelector('.product-slider-nav-title') as HTMLElement;
    const titleLoc = title?.dataset.title || ''

    if (title && titleLoc) {
      let resultsText = titleLoc.replace(/(\%number\% \S*)/, '<span class="green-text">$1</span>');
      resultsText = resultsText.replace(/\%number\%/, slides.length);
      if (slides.length === 1) resultsText = resultsText.replace(/\(.*\)/, '');
      if (slides.length > 1) resultsText = resultsText.replace(/\(|\)/g, '');
      title.innerHTML = resultsText;
    }

    title?.after(prevButton);
    prevButton?.after(nextButton);
  }
}

/**
 * Initializes functionality
 *
 * @param {DOMElement} elements - Product slider elements
 */
export async function init(elements) {
  console.log('productSlider.init()');
  elements.forEach(element => {
    new ProductSlider(element);
  });
}

export default init;
